"use client"

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createContext } from "use-context-selector";
import { apiPost } from "@/lib/api-post";
import { deepCloneJSON } from "@/lib/duplicate-object";
import { CURRENT_PREFERENCES_VERSION, DEFAULT_USER_DISPLAY_PREFERENCES } from "@/user-preferences/default-user-preferences";
import type { UserPreferences } from "@/user-preferences/types";
import { loadAndNormalizeServerPreferences, normalizeUserPreferences } from "./normalize-user-preferences";
import { PreferencesCorruptionDialog } from "./preferences-corruption-dialog";

type PreferencesUpdater = (updater: (prev: UserPreferences) => UserPreferences) => Promise<void>;

export interface PreferencesStoreValue {
    preferences: UserPreferences;
    isLoading: boolean;
    isInitialized: boolean;
    updatePreferences: PreferencesUpdater;
    resetToDefaults: () => Promise<void>;
}

const PreferencesStoreContext = createContext<PreferencesStoreValue | null>(null);

export function UserPreferencesStoreProvider({
    children,
    serverUserPreferences,
}: {
    children: React.ReactNode;
    serverUserPreferences?: UserPreferences | null;
}) {
    const initial = loadAndNormalizeServerPreferences(serverUserPreferences ?? null);
    const [preferences, setPreferences] = useState<UserPreferences>(initial.preferences);
    const [corruptionDialogOpen, setCorruptionDialogOpen] = useState(initial.showCorruptionWarning);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    const preferencesRef = useRef(preferences);
    preferencesRef.current = preferences;

    const savingRef = useRef(false);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const savePreferences = useCallback(async (prefs: UserPreferences) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(async () => {
            if (savingRef.current) return;
            try {
                savingRef.current = true;
                await apiPost("profile/save-display-preferences", prefs);
            } catch (e) {
                console.error("Error saving preferences:", e);
            } finally {
                savingRef.current = false;
            }
        }, 800);
    }, []);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!serverUserPreferences) {
            const next = loadAndNormalizeServerPreferences(null);
            setPreferences(next.preferences);
            setCorruptionDialogOpen(next.showCorruptionWarning);
            setIsInitialized(true);
            return;
        }

        setIsLoading(true);
        try {
            const sessionPrefs = serverUserPreferences;
            const { preferences: finalPrefs, showCorruptionWarning } =
                loadAndNormalizeServerPreferences(sessionPrefs);

            const needsPersist =
                !("version" in sessionPrefs) ||
                (sessionPrefs as UserPreferences).version < CURRENT_PREFERENCES_VERSION ||
                JSON.stringify(sessionPrefs) !== JSON.stringify(finalPrefs);

            if (needsPersist) {
                void savePreferences(finalPrefs);
            }

            setPreferences(finalPrefs);
            setCorruptionDialogOpen(showCorruptionWarning);
        } catch (e) {
            console.error("Error initializing preferences:", e);
            const fallback = loadAndNormalizeServerPreferences(null).preferences;
            setPreferences(fallback);
            setCorruptionDialogOpen(true);
        } finally {
            setIsLoading(false);
            setIsInitialized(true);
        }
    }, [serverUserPreferences, savePreferences]);

    const updatePreferences: PreferencesUpdater = useCallback(async (updater) => {
        setPreferences((prev) => {
            try {
                const next = updater(prev);
                const { preferences: safe } = normalizeUserPreferences(next);
                void savePreferences(safe);
                return safe;
            } catch (e) {
                console.error("Error updating preferences:", e);
                const { preferences: safe } = normalizeUserPreferences(prev);
                return safe;
            }
        });
    }, [savePreferences]);

    const resetToDefaults = useCallback(async () => {
        const next = deepCloneJSON(DEFAULT_USER_DISPLAY_PREFERENCES);
        setPreferences(next);
        setCorruptionDialogOpen(false);
        await savePreferences(next);
    }, [savePreferences]);

    const handleKeepRepaired = useCallback(() => {
        setCorruptionDialogOpen(false);
        void savePreferences(preferencesRef.current);
    }, [savePreferences]);

    const value = useMemo<PreferencesStoreValue>(() => ({
        preferences,
        isLoading,
        isInitialized,
        updatePreferences,
        resetToDefaults,
    }), [preferences, isLoading, isInitialized, updatePreferences, resetToDefaults]);

    return (
        <PreferencesStoreContext.Provider value={value}>
            {children}
            <PreferencesCorruptionDialog
                open={corruptionDialogOpen}
                onOpenChange={setCorruptionDialogOpen}
                onKeepRepaired={handleKeepRepaired}
                onResetToDefaults={resetToDefaults}
            />
        </PreferencesStoreContext.Provider>
    );
}

export { PreferencesStoreContext };
